import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { DemoLiveProvider, useDemoLive, type DemoCallDoc, type DemoCallTurn, type DemoRiskEvent } from "./demo/demo-live-provider";
import { StatusStrip, type SupervisionViewMode } from "./status-strip";
import { CallTileV2 } from "./wall/call-tile-v2";
import { CallDrawerV2 } from "./drawer/call-drawer-v2";
import { CallDrawer } from "./drawer/call-drawer";
import { AlertQueueV2 } from "./queue/alert-queue-v2";
import { FlaggedCalls } from "./flagged-calls";
import { LiveInsights } from "./insights/live-insights";
import { ShiftLobby } from "./shift-lobby";
import { getGridDimensions } from "./wall/call-wall";
import { useSupervisionStream } from "./use-supervision-stream";
import { usePersistentState } from "../../hooks/use-persistent-state";
import type { SupervisedCall } from "./types";
import type { SupervisionSection } from "./supervision-section";

const ORDER: Record<string, number> = { red: 0, amber: 1, green: 2 };

function adaptRealCallToDemoDoc(real: SupervisedCall): DemoCallDoc & { is_real: boolean } {
  const alertSev =
    real.severity_open ??
    real.alerts?.top?.severity ??
    real.severity ??
    null;

  const isRed =
    real.risk?.level === "red" ||
    alertSev === "critical" ||
    (real as any).risk_tier === "red";

  const isAmber =
    !isRed &&
    (real.risk?.level === "amber" ||
      alertSev === "warn" ||
      (real as any).risk_tier === "amber" ||
      (real.alerts?.open ?? 0) > 0 ||
      Boolean(real.alerts?.top));

  const tier: "green" | "amber" | "red" = isRed ? "red" : isAmber ? "amber" : "green";

  const score = real.risk?.score ?? (tier === "red" ? 60 : tier === "amber" ? 30 : 0);

  const turns: DemoCallTurn[] = (real.recent || []).map((m, idx) => ({
    seq: idx + 1,
    speaker: m.role === "assistant" ? "ai" : "customer",
    text: m.text,
    ts: m.created_at,
  }));

  const events: DemoRiskEvent[] = [];
  if (real.alerts?.top) {
    events.push({
      id: real.alerts.top.alert_id,
      call_id: real.call_id,
      rule_id: real.alerts.top.type,
      rule_label: real.alerts.top.detail,
      tier:
        real.alerts.top.severity === "critical"
          ? "red"
          : real.alerts.top.severity === "warn"
          ? "amber"
          : "green",
      score_delta: score,
      evidence_turn_seq: 1,
      ts: real.alerts.top.first_seen_at || new Date().toISOString(),
    });
  }

  const rawState = (real.lifecycle || real.status || "live").toLowerCase();
  const state = (
    rawState.includes("dial")
      ? "dialing"
      : rawState.includes("ring")
      ? "ringing"
      : rawState.includes("end")
      ? "ended"
      : rawState.includes("wrap")
      ? "wrapping"
      : real.holder
      ? "human_controlled"
      : "live"
  ) as DemoCallDoc["state"];

  return {
    id: real.call_id,
    call_id: real.call_id,
    campaign_name: real.campaign || real.agent_name || "Direct Dial",
    customer_name: real.customer_name || real.contact_name || "Live Customer",
    customer_ref: real.customer_phone || real.contact_id || real.call_id,
    language: "en",
    direction: "outbound",
    started_at: real.created_at || new Date().toISOString(),
    state,
    risk_tier: tier,
    risk_score: score,
    risk_reasons: real.alerts?.top ? [real.alerts.top.type] : [],
    turns,
    events,
    sentiment_trend: [0.1],
    context: {
      account_state: "active",
      consent_status: "recorded",
      in_calling_window: true,
      objective: `Live call with ${real.customer_name || real.contact_name || "customer"}`,
      last_interactions: [],
    },
    is_real: true,
  };
}

function sortDemoCalls(list: any[]) {
  return [...list].sort((a, b) => {
    // Real calls always sort to the front of their respective tier
    if (Boolean(a.is_real) !== Boolean(b.is_real)) {
      return a.is_real ? -1 : 1;
    }

    const tierA = a.risk_tier ?? "green";
    const tierB = b.risk_tier ?? "green";
    const tierDiff = (ORDER[tierA] ?? 2) - (ORDER[tierB] ?? 2);
    if (tierDiff !== 0) return tierDiff;

    // Within flagged cards (red/amber), rotate the most recently flagged call to the front!
    if (tierA !== "green" || tierB !== "green") {
      const aEvents = a.events || [];
      const bEvents = b.events || [];
      const aLastFlag = aEvents.length ? new Date(aEvents[aEvents.length - 1].ts || 0).getTime() : 0;
      const bLastFlag = bEvents.length ? new Date(bEvents[bEvents.length - 1].ts || 0).getTime() : 0;
      if (aLastFlag !== bLastFlag) {
        return bLastFlag - aLastFlag; // Newest flag rotates to the very top!
      }
      return (b.risk_score || 0) - (a.risk_score || 0);
    }

    // Within green tier, sort by start time descending so fresh incoming calls rotate in cleanly
    return new Date(b.started_at || 0).getTime() - new Date(a.started_at || 0).getTime();
  });
}

function DemoConsoleInner({
  onShift,
  setOnShift,
  comfortableLoad,
  setComfortableLoad,
  section,
  setSection,
  mobileReviewNavigation,
}: {
  onShift: boolean;
  setOnShift: (val: boolean) => void;
  comfortableLoad: number;
  setComfortableLoad: (val: number) => void;
  section: SupervisionSection;
  setSection: (val: SupervisionSection) => void;
  mobileReviewNavigation: React.ReactNode;
}) {
  const { calls: demoCallsMap, rules } = useDemoLive();
  const { calls: realCallsList } = useSupervisionStream(true);
  const [tab, setTab] = useState<SupervisionViewMode>("wall");
  const [focusId, setFocusId] = useState<string | null>(null);

  // Active real calls from telephony/SIP/Twilio
  const activeRealCalls = useMemo(() => {
    return (realCallsList || []).filter(
      (c) =>
        !c.lifecycle?.startsWith("ended") &&
        c.status !== "completed" &&
        c.status !== "failed"
    );
  }, [realCallsList]);

  // Adapt real calls into DemoCallDoc format for uniform rendering
  const adaptedRealCalls = useMemo(() => {
    return activeRealCalls.map(adaptRealCallToDemoDoc);
  }, [activeRealCalls]);

  const targetCapacity = Math.max(1, Math.min(25, comfortableLoad));

  // Hybrid merge: Real calls are placed first, and remaining slots up to targetCapacity are filled by demo calls
  const mergedCalls = useMemo(() => {
    const demoList = Object.values(demoCallsMap);
    if (adaptedRealCalls.length === 0) {
      return sortDemoCalls(demoList).slice(0, targetCapacity);
    }
    const realIds = new Set(adaptedRealCalls.map((c) => c.id));
    const fillsNeeded = Math.max(0, targetCapacity - adaptedRealCalls.length);
    const fills = demoList.filter((c) => !realIds.has(c.id)).slice(0, fillsNeeded);
    return sortDemoCalls([...adaptedRealCalls, ...fills]).slice(0, targetCapacity);
  }, [adaptedRealCalls, demoCallsMap, targetCapacity]);

  const focusedRealCall = useMemo(() => {
    if (!focusId) return null;
    return activeRealCalls.find((c) => c.call_id === focusId) || null;
  }, [focusId, activeRealCalls]);

  const counts = useMemo(
    () => ({
      live: mergedCalls.length,
      critical: mergedCalls.filter((c) => c.risk_tier === "red").length,
      warning: mergedCalls.filter((c) => c.risk_tier === "amber").length,
      overdue: 0,
      acknowledged: 0,
    }),
    [mergedCalls],
  );

  const headroomObj = useMemo(
    () => ({
      supervisors: 1,
      capacity: targetCapacity,
      live_calls: mergedCalls.length,
      utilisation: Math.min(1, mergedCalls.length / targetCapacity),
    }),
    [mergedCalls.length, targetCapacity],
  );

  const ruleLabels = useMemo(() => {
    const m: Record<string, string> = {};
    Object.values(rules).forEach((r: any) => {
      m[r.id] = r.label;
    });
    return m;
  }, [rules]);

  const [selectedFlaggedCall, setSelectedFlaggedCall] = useState<SupervisedCall | null>(null);

  const supervisedCallsForInsights = useMemo(() => {
    return mergedCalls.map((c: any) => {
      const isRed = c.risk_tier === "red";
      const isAmber = c.risk_tier === "amber";
      const topAlert = c.events?.[0] ? {
        alert_id: c.events[0].id,
        type: c.events[0].rule_id,
        severity: isRed ? "critical" as const : "warn" as const,
        status: "open" as const,
        detail: c.events[0].rule_label,
        quoted_text: "",
        first_seen_at: c.events[0].ts,
      } : null;

      return {
        call_id: c.id,
        contact_name: c.customer_name || c.customer_ref || "Customer",
        customer_name: c.customer_name,
        customer_phone: c.customer_ref,
        campaign: c.campaign_name,
        created_at: c.started_at,
        severity: isRed ? "critical" : isAmber ? "warn" : "info",
        severity_open: isRed ? "critical" : isAmber ? "warn" : null,
        risk: { score: c.risk_score || 0, level: c.risk_tier || "green" },
        message_count: c.turns?.length || 0,
        alerts: {
          open: topAlert ? 1 : 0,
          acknowledged: 0,
          overdue: 0,
          total: topAlert ? 1 : 0,
          top: topAlert,
        },
        is_demo: true,
      } as SupervisedCall;
    });
  }, [mergedCalls]);

  if (section === "flagged") {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col bg-canvas overflow-hidden">
        {mobileReviewNavigation}
        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden p-4">
          <FlaggedCalls onSelect={(c) => setSelectedFlaggedCall(c)} />
          <AnimatePresence>
            {selectedFlaggedCall && (
              <CallDrawer
                call={selectedFlaggedCall}
                onClose={() => setSelectedFlaggedCall(null)}
                languageMode="both"
                onLanguageModeChange={() => {}}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  if (section === "insights") {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-canvas">
        {mobileReviewNavigation}
        <LiveInsights
          calls={supervisedCallsForInsights}
          currentUserId={null}
          headroom={{
            supervisors: 1,
            capacity: targetCapacity,
            live_calls: mergedCalls.length,
            utilisation: Math.min(1, mergedCalls.length / targetCapacity),
            headroom_pct: Math.max(0, 1 - mergedCalls.length / targetCapacity),
          } as any}
          onOpenCall={(callId) => {
            setFocusId(callId);
            setSection("live");
          }}
        />
      </div>
    );
  }

  if (!onShift) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas">
        {mobileReviewNavigation}
        <ShiftLobby
          rota={{
            mine: {
              user_id: "demo_sup",
              display_name: "You",
              comfortable_load: comfortableLoad,
              started_at: new Date().toISOString(),
            },
            on_shift: [],
            handover: {
              live_calls: 0,
              open_alerts: 0,
              overdue_alerts: 0,
              held_calls: [],
            },
          } as any}
          busy={false}
          error={null}
          comfortableLoad={comfortableLoad}
          onComfortableLoadChange={setComfortableLoad}
          onStart={() => setOnShift(true)}
        />
      </div>
    );
  }

  const { cols, rows } = getGridDimensions(mergedCalls.length);

  return (
    <div className="h-full flex flex-col bg-background text-foreground overflow-hidden" data-testid="supervisor-demo-console">
      {mobileReviewNavigation}
      {/* Topbar with Wall/Queue pill toggle in exact same location */}
      <div className="p-2 sm:p-3 shrink-0">
        <StatusStrip
          counts={counts}
          status="live"
          view={tab}
          onViewChange={setTab}
          declaredCapacity={targetCapacity}
          headroom={headroomObj}
          onEndShift={() => setOnShift(false)}
          isDemoMode={true}
        />
      </div>

      {/* Main Workspace */}
      <div className="relative flex-1 min-h-0 flex overflow-hidden px-2 sm:px-3 pb-2 sm:pb-3">
        <div className="w-full h-full flex-1 min-h-0 overflow-y-auto hide-scrollbar [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {tab === "wall" && (
            <div className="w-full h-full flex flex-1 flex-col min-h-0">
              <div
                className="grid gap-2 w-full h-full flex-1 min-h-0"
                style={{
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
                }}
                data-testid="call-grid"
              >
                {mergedCalls.map((c) => (
                  <CallTileV2
                    key={c.id}
                    call={c}
                    ruleLabels={ruleLabels}
                    onOpen={(id) => setFocusId(id)}
                  />
                ))}
              </div>
              {mergedCalls.length === 0 && (
                <div className="h-64 flex flex-col items-center justify-center text-muted-foreground text-xs font-mono">
                  <span>NO LIVE CALLS</span>
                </div>
              )}
            </div>
          )}

          {tab === "queue" && (
            <AlertQueueV2 calls={mergedCalls} onSelectCall={(id) => { setFocusId(id); }} />
          )}
        </div>

        {/* Real-time Call Drawer — Layer above the call cards */}
        <AnimatePresence>
          {focusedRealCall ? (
            <CallDrawer
              call={focusedRealCall}
              onClose={() => setFocusId(null)}
              languageMode="both"
              onLanguageModeChange={() => {}}
            />
          ) : focusId ? (
            <CallDrawerV2 callId={focusId} onClose={() => setFocusId(null)} />
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}

export function DemoSupervisionConsole() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [onShift, setOnShift] = usePersistentState<boolean>("demo.supervision.onShift", false);
  const [comfortableLoad, setComfortableLoad] = usePersistentState<number>(
    "demo.supervision.comfortableLoad",
    9,
  );

  const section: SupervisionSection = useMemo(() => {
    if (pathname.startsWith("/supervision/flagged")) return "flagged";
    if (pathname.startsWith("/supervision/insights")) return "insights";
    return "live";
  }, [pathname]);

  const setSection = (next: SupervisionSection) => {
    if (next === "flagged") navigate("/supervision/flagged");
    else if (next === "insights") navigate("/supervision/insights");
    else navigate("/supervision");
  };

  const mobileReviewNavigation = (
    <nav
      aria-label="Supervision sections"
      className="flex shrink-0 gap-1 border-b border-line bg-elevated px-3 py-1.5 sm:hidden"
    >
      <button
        type="button"
        className={
          section === "live"
            ? "rounded-md bg-soft px-2.5 py-1 text-xs font-medium text-ink"
            : "px-2.5 py-1 text-xs text-ink-3"
        }
        aria-pressed={section === "live"}
        onClick={() => setSection("live")}
      >
        Live
      </button>
      <button
        type="button"
        className={
          section === "flagged"
            ? "rounded-md bg-soft px-2.5 py-1 text-xs font-medium text-ink"
            : "px-2.5 py-1 text-xs text-ink-3"
        }
        aria-pressed={section === "flagged"}
        onClick={() => setSection("flagged")}
      >
        Flagged
      </button>
      <button
        type="button"
        className={
          section === "insights"
            ? "rounded-md bg-soft px-2.5 py-1 text-xs font-medium text-ink"
            : "px-2.5 py-1 text-xs text-ink-3"
        }
        aria-pressed={section === "insights"}
        onClick={() => setSection("insights")}
      >
        Insights
      </button>
    </nav>
  );

  return (
    <DemoLiveProvider>
      <DemoConsoleInner
        onShift={onShift}
        setOnShift={setOnShift}
        comfortableLoad={comfortableLoad}
        setComfortableLoad={setComfortableLoad}
        section={section}
        setSection={setSection}
        mobileReviewNavigation={mobileReviewNavigation}
      />
    </DemoLiveProvider>
  );
}
